import { createContext, ReactNode, useContext, useLayoutEffect, useMemo } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  DocumentationApiTargets,
  manualDocumentationApiTargets,
  releaseDocumentationApiTargets,
  setDocumentationApiTargets,
} from '../../services/documentationApi';

export type DocumentationShellValue = {
  kind: 'tenant' | 'platform';
  basePath: string;
  moduleCode: string;
  canWrite: boolean;
  showSalesProduct: boolean;
  allowExpiration: boolean;
  treeWidthKey: string;
  treeCollapsedKey: string;
  homeTitle: string;
  homeDescription: string;
  emptyMessage: string;
};

const tenantShell: DocumentationShellValue = {
  kind: 'tenant',
  basePath: '/technology/documentation',
  moduleCode: 'technology.documentation',
  canWrite: false,
  showSalesProduct: true,
  allowExpiration: true,
  treeWidthKey: 'technology.documentation.tree-width',
  treeCollapsedKey: 'technology.documentation.tree-collapsed',
  homeTitle: 'Documentação',
  homeDescription:
    'Documentos e wikis com páginas relacionadas, classificadores, tags, status e links públicos para clientes.',
  emptyMessage: 'Nenhum documento ainda. Crie o primeiro acima.',
};

const DocumentationShellContext = createContext<DocumentationShellValue>(tenantShell);

export function useDocumentationShell(): DocumentationShellValue {
  const shell = useContext(DocumentationShellContext);
  const { canWriteModule } = usePermissions();
  if (shell.kind === 'tenant') {
    return {
      ...shell,
      canWrite: canWriteModule(shell.moduleCode),
    };
  }

  return shell;
}

export function ManualDocumentationShell({ children }: { children?: ReactNode }) {
  const { canWriteModule, access } = usePermissions();
  const canWrite = Boolean(access?.isOwnerCustomer) && canWriteModule('manual');
  const targets = manualDocumentationApiTargets;

  useLayoutEffect(() => {
    setDocumentationApiTargets(targets);
    return () => setDocumentationApiTargets(null);
  }, [targets]);

  const value = useMemo<DocumentationShellValue>(
    () => ({
      kind: 'platform',
      basePath: '/manual',
      moduleCode: 'manual',
      canWrite,
      showSalesProduct: false,
      allowExpiration: true,
      treeWidthKey: 'manual.tree-width',
      treeCollapsedKey: 'manual.tree-collapsed',
      homeTitle: 'Manual',
      homeDescription: 'Manuais da ferramenta, com páginas publicadas para todas as organizações.',
      emptyMessage: canWrite
        ? 'Nenhum manual ainda. Crie o primeiro acima.'
        : 'Nenhum manual publicado ainda.',
    }),
    [canWrite],
  );

  return (
    <DocumentationShellContext.Provider value={value}>
      {children ?? <Outlet />}
    </DocumentationShellContext.Provider>
  );
}

export function ReleaseDocumentationShell({ children }: { children?: ReactNode }) {
  const targets = releaseDocumentationApiTargets;

  useLayoutEffect(() => {
    setDocumentationApiTargets(targets);
    return () => setDocumentationApiTargets(null);
  }, [targets]);

  const value = useMemo<DocumentationShellValue>(
    () => ({
      kind: 'tenant',
      basePath: '/technology/deployments/documentation',
      moduleCode: 'technology.deployments',
      canWrite: false,
      showSalesProduct: true,
      allowExpiration: false,
      treeWidthKey: 'technology.deployments.documentation.tree-width',
      treeCollapsedKey: 'technology.deployments.documentation.tree-collapsed',
      homeTitle: 'Releases',
      homeDescription:
        'Registro permanente do que entrou em cada release. A geração a partir dos itens do Azure entra depois; por enquanto o conteúdo é escrito aqui.',
      emptyMessage: 'Nenhuma documentação de release ainda. Crie a primeira acima.',
    }),
    [],
  );

  return (
    <DocumentationShellContext.Provider value={value}>
      {children ?? <Outlet />}
    </DocumentationShellContext.Provider>
  );
}

export function ManualOwnerModuleRoute({ moduleCode }: { moduleCode: string }) {
  const { isLoading, access, canAccessModule } = usePermissions();

  if (isLoading) {
    return <LoadingSpinner label="Carregando permissões..." />;
  }

  if (!access?.isOwnerCustomer || !canAccessModule(moduleCode)) {
    return <Navigate to="/manual" replace />;
  }

  return <Outlet />;
}

export type { DocumentationApiTargets };
